import { useTranslation } from "react-i18next";import React from 'react';
import { cn } from '@/shared/utils';
import {
  Package,
  Truck,
  MapPin,
  Clock,
  Thermometer,
  Navigation,
  CheckCircle,
  AlertCircle,
  ChevronRight,
  Play } from
'lucide-react';
import { Badge } from '@/shared/components/ui/badge';
import { Button } from '@/shared/components/ui/button';

// Uber-style shipment card for replay selection
export const ShipmentCard = ({
  shipment,
  isSelected = false,
  onSelect,
  onReplay,
  compact = false
}) => {const { t } = useTranslation("ezparcel");
  const statusStyles = {
    'Delivered': { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', icon: CheckCircle },
    'In Transit': { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', icon: Truck },
    'Pending': { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', icon: Clock },
    'Failed': { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', icon: AlertCircle }
  };

  const status = statusStyles[shipment.status] || statusStyles['Pending'];
  const StatusIcon = status.icon;

  if (compact) {
    return (
      <div
        onClick={() => onSelect?.(shipment)}
        className={cn(
          "flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all duration-200",
          isSelected ?
          "border-blue-300 bg-blue-50/50 shadow-md" :
          "border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm"
        )}
        data-testid={`shipment-card-${shipment.id}`}>
        
        {/* Icon */}
        <div className={cn(
          "w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0",
          status.bg
        )}>
          <Package className={cn("h-5 w-5", status.text)} />
        </div>
        
        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-sm text-gray-900 truncate">
              {shipment.name}
            </span>
            <Badge variant="outline" className={cn("text-[10px] h-5", status.text, status.bg, status.border)}>
              {shipment.status}
            </Badge>
          </div>
          <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
            <span className="flex items-center gap-1">
              <MapPin className="h-3 w-3" />
              {shipment.stops}{t("stops", "stops")}
            </span>
            <span>{shipment.distance}{t("km", "km")}</span>
          </div>
        </div>
        
        {/* Arrow */}
        <ChevronRight className={cn(
          "h-5 w-5 flex-shrink-0 transition-transform",
          isSelected ? "text-blue-500 translate-x-1" : "text-gray-400"
        )} />
      </div>);

  }

  return (
    <div
      className={cn(
        "rounded-xl border overflow-hidden transition-all duration-200",
        isSelected ?
        "border-blue-300 shadow-lg ring-2 ring-blue-100" :
        "border-gray-200 hover:border-gray-300 hover:shadow-md"
      )}
      data-testid={`shipment-card-${shipment.id}`}>
      
      {/* Header */}
      <div
        className={cn(
          "p-4 cursor-pointer",
          isSelected ? "bg-blue-50/50" : "bg-white"
        )}
        onClick={() => onSelect?.(shipment)}>
        
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            {/* Status indicator */}
            <div className={cn(
              "w-12 h-12 rounded-xl flex items-center justify-center",
              status.bg
            )}>
              <StatusIcon className={cn("h-6 w-6", status.text)} />
            </div>
            
            {/* Details */}
            <div>
              <h3 className="font-semibold text-gray-900">{shipment.name}</h3>
              <p className="text-sm text-gray-500 mt-0.5">{shipment.id}</p>
              <div className="flex items-center gap-2 mt-2">
                <Badge variant="outline" className={cn(
                  "font-medium",
                  status.text, status.bg, status.border
                )}>
                  {shipment.status}
                </Badge>
                <span className="text-xs text-gray-400">{shipment.date}</span>
              </div>
            </div>
          </div>
          
          {/* Replay button */}
          <Button
            size="sm"
            variant={isSelected ? "default" : "outline"}
            className="gap-1"
            onClick={(e) => {
              e.stopPropagation();
              onReplay?.(shipment);
            }}
            data-testid={`replay-btn-${shipment.id}`}>
            
            <Play className="h-4 w-4" />{t("replay", "Replay")}

          </Button>
        </div>
      </div>
      
      {/* Stats row */}
      <div className="grid grid-cols-4 divide-x divide-gray-100 border-t border-gray-100 bg-gray-50/50">
        <div className="p-3 text-center">
          <div className="flex items-center justify-center gap-1 text-gray-400 mb-1">
            <Truck className="h-3.5 w-3.5" />
          </div>
          <div className="text-sm font-semibold text-gray-900">{shipment.driver}</div>
          <div className="text-xs text-gray-500">{t("driver", "Driver")}</div>
        </div>
        
        <div className="p-3 text-center">
          <div className="flex items-center justify-center gap-1 text-gray-400 mb-1">
            <MapPin className="h-3.5 w-3.5" />
          </div>
          <div className="text-sm font-semibold text-gray-900">{shipment.stops}</div>
          <div className="text-xs text-gray-500">{t("stops", "Stops")}</div>
        </div>
        
        <div className="p-3 text-center">
          <div className="flex items-center justify-center gap-1 text-gray-400 mb-1">
            <Navigation className="h-3.5 w-3.5" />
          </div>
          <div className="text-sm font-semibold text-gray-900">{shipment.distance}{t("km", "km")}</div>
          <div className="text-xs text-gray-500">{t("distance", "Distance")}</div>
        </div>
        
        <div className="p-3 text-center">
          <div className="flex items-center justify-center gap-1 text-gray-400 mb-1">
            <Clock className="h-3.5 w-3.5" />
          </div>
          <div className="text-sm font-semibold text-gray-900">{shipment.duration}</div>
          <div className="text-xs text-gray-500">{t("duration", "Duration")}</div>
        </div>
      </div>
      
      {/* Trail indicators */}
      <div className="flex items-center justify-between px-4 py-3 bg-white border-t border-gray-100">
        <div className="flex items-center gap-3">
          {shipment.hasGPS &&
          <div className="flex items-center gap-1 text-xs text-emerald-600">
              <Navigation className="h-3.5 w-3.5" />
              <span>{t("gpsTrail", "GPS Trail")}</span>
            </div>
          }
          {shipment.hasTemp &&
          <div className="flex items-center gap-1 text-xs text-blue-600">
              <Thermometer className="h-3.5 w-3.5" />
              <span>{t("tempTrail", "Temp Trail")}</span>
            </div>
          }
        </div>
        
        {shipment.events > 0 &&
        <Badge variant="secondary" className="text-xs">
            {shipment.events}{t("events", "events")}
        </Badge>
        }
      </div>
    </div>);

};

// List view for multiple shipments
export const ShipmentList = ({
  shipments = [],
  selectedId,
  onSelect,
  onReplay,
  compact = false
}) => {const { t } = useTranslation("ezparcel");
  if (shipments.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <Package className="h-10 w-10 mx-auto mb-3 opacity-50" />
        <p className="font-medium">{t("noShipmentsAvailable", "No shipments available")}</p>
        <p className="text-sm mt-1">{t("shipmentsWithTrail", "Shipments with trail data will appear here")}</p>
      </div>);

  }

  return (
    <div className={cn("space-y-3", compact && "space-y-2")}>
      {shipments.map((shipment) =>
      <ShipmentCard
        key={shipment.id}
        shipment={shipment}
        isSelected={selectedId === shipment.id}
        onSelect={onSelect}
        onReplay={onReplay}
        compact={compact} />

      )}
    </div>);

};